import { useCallback, useState } from "react";

import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { SearchBox } from "@/components/pages_logged_in/workspace-admin/SearchBox";
import { Filter } from "@/components/pieces/filter/Filter";
import { FilterButton } from "@/components/pieces/filter/FilterButton";
import { SettingSvg } from "@/components/pieces/icons/svg/setting";
import { RubricDetailQuery } from "@/graphql";

import { RubricsList } from "./RubricsList";

type Rubric = RubricDetailQuery["rubric"];

const sources = [
  { id: null, name: "All" },
  { id: "global", name: "Core" },
  { id: "workspace", name: "Workspace" },
];

interface RubricsControllerProps {
  onSelectRubric: (rubric: Rubric) => void;
  excludedIds: string[];
}

export function RubricsController({
  onSelectRubric,
  excludedIds,
}: RubricsControllerProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [source, setSource] = useState<string | null>(null);
  const pageSize = 5;

  const refine = useCallback(
    (query: string) => {
      setQuery(query);
      setCurrentPage(1);
    },
    [setQuery, setCurrentPage],
  );

  const handleDataLoaded = useCallback(
    ({ totalRecords }: { totalRecords: number }) => {
      setTotalRecords(totalRecords);
    },
    [setTotalRecords],
  );

  const handleSourceChange = useCallback(
    (source: string | null) => {
      setSource(source);
      setCurrentPage(1);
    },
    [setSource, setCurrentPage],
  );

  const handlePreview = useCallback(
    (rubric: Rubric) => {
      onSelectRubric(rubric);
    },
    [onSelectRubric],
  );

  const sourceMenuLabel = sources.find((s) => s.id === source)?.name || "All";
  const borderStyle = { border: "0.5px solid #E2E2E2", borderRadius: 6 };
  const style = { padding: "10px 16px" };
  const options = sources.map((s) => ({
    id: s.id ?? "",
    name: s.name,
  }));
  const selectedOptions = options.filter((s) => s.id === source);

  return (
    <div className="flex h-full min-h-0 flex-col items-start gap-4 p-6 pr-3">
      <div className="flex w-full gap-2">
        <Filter
          button={
            <FilterButton
              style={{ ...style, ...borderStyle }}
              rightIcon={<SettingSvg />}
              text={sourceMenuLabel}
            />
          }
          options={options}
          selectedOptions={selectedOptions}
          onFilterChange={(option) => handleSourceChange(option.id)}
          multiple={false}
          menuPosition="bottom-end"
          viewMode="radio"
          fullHeight={true}
        />
        <SearchBox
          style={{
            borderRadius: 6,
            border: "0.5px solid #E2E2E2",
            padding: "22px 0px 22px 36px",
            fontSize: 14,
            flexGrow: 1,
          }}
          className="icon-center"
          refine={refine}
          throttledDuration={400}
          searchPlaceholder="Search Rubrics..."
        />
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col justify-between gap-4">
        <RubricsList
          currentPage={currentPage}
          query={query}
          source={source}
          onDataLoaded={handleDataLoaded}
          onPreview={handlePreview}
          excludedIds={excludedIds}
        />
        <Paging
          currentPage={currentPage}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          totalRecords={totalRecords}
        />
      </div>
    </div>
  );
}
